import React, { useState, useEffect } from 'react';
import styles from '../Insights.module.css';

const TimeFilter = ({ 
  period = 'month', 
  onPeriodChange,
  onCustomRange,
  firstLogDate = null,
  monthsWithData = [] // Array of "YYYY-MM" strings that have data
}) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-11
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [yearOptions, setYearOptions] = useState([]);
  
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Generate year options from firstLogDate to current year
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    let startYear = currentYear;
    
    if (firstLogDate) {
      const firstDate = new Date(firstLogDate);
      startYear = firstDate.getFullYear();
    }
    
    const options = [];
    for (let year = startYear; year <= currentYear; year++) {
      options.push(year);
    }
    setYearOptions(options);
  }, [firstLogDate]);

  // Check if a month has data
  const hasDataForMonth = (monthIndex, year) => {
    const monthStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    return monthsWithData.includes(monthStr);
  };

  // Check if a year has any data
  const hasDataForYear = (year) => {
    return monthsWithData.some(m => m.startsWith(`${year}-`));
  };

  // Check if month is in the future
  const isFutureMonth = (monthIndex, year) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    if (year > currentYear) return true;
    if (year === currentYear && monthIndex > currentMonth) return true;
    return false;
  };

  // Check if month is before first log
  const isBeforeFirstLog = (monthIndex, year) => {
    if (!firstLogDate) return false;
    const firstDate = new Date(firstLogDate);
    const firstYear = firstDate.getFullYear();
    const firstMonth = firstDate.getMonth();
    
    if (year < firstYear) return true;
    if (year === firstYear && monthIndex < firstMonth) return true;
    return false;
  };
  
  const handlePeriodClick = (newPeriod) => {
    if (newPeriod === period) {
      // Toggle picker if clicking same period
      if (newPeriod === 'month') {
        setShowMonthPicker(!showMonthPicker);
        setShowYearPicker(false);
      } else if (newPeriod === 'year') {
        setShowYearPicker(!showYearPicker);
        setShowMonthPicker(false);
      }
    } else {
      // Switch to new period
      setShowMonthPicker(false);
      setShowYearPicker(false);
      onPeriodChange(newPeriod);
      
      // Auto-fetch current period data
      if (newPeriod === 'month') {
        const now = new Date();
        setSelectedMonth(now.getMonth());
        setSelectedYear(now.getFullYear());
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        onCustomRange && onCustomRange(start, 'start');
        onCustomRange && onCustomRange(end, 'end');
      } else if (newPeriod === 'year') {
        const now = new Date();
        setSelectedYear(now.getFullYear());
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear(), 11, 31);
        onCustomRange && onCustomRange(start, 'start');
        onCustomRange && onCustomRange(end, 'end');
      }
    }
  };

  const handleMonthSelect = (monthIndex) => {
    // Don't select if disabled
    if (isFutureMonth(monthIndex, selectedYear) || isBeforeFirstLog(monthIndex, selectedYear)) {
      return;
    }
    
    setSelectedMonth(monthIndex);
    const start = new Date(selectedYear, monthIndex, 1);
    const end = new Date(selectedYear, monthIndex + 1, 0);
    onCustomRange && onCustomRange(start, 'start');
    onCustomRange && onCustomRange(end, 'end');
    setShowMonthPicker(false);
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    
    // If current month is in the future for new year, adjust to latest valid month
    let adjustedMonth = selectedMonth;
    if (isFutureMonth(selectedMonth, year)) {
      adjustedMonth = new Date().getMonth();
      setSelectedMonth(adjustedMonth);
    }
    
    if (period === 'month') {
      const start = new Date(year, adjustedMonth, 1);
      const end = new Date(year, adjustedMonth + 1, 0);
      onCustomRange && onCustomRange(start, 'start');
      onCustomRange && onCustomRange(end, 'end');
    } else if (period === 'year') {
      const start = new Date(year, 0, 1);
      const end = new Date(year, 11, 31);
      onCustomRange && onCustomRange(start, 'start');
      onCustomRange && onCustomRange(end, 'end');
    }
    setShowYearPicker(false);
  };

  const getButtonLabel = (btnPeriod) => {
    if (btnPeriod === 'week') return 'This Week';
    if (btnPeriod === 'month') {
      if (period === 'month') {
        return `${months[selectedMonth]} ${selectedYear}`;
      }
      return 'Month';
    }
    if (btnPeriod === 'year') {
      if (period === 'year') {
        return selectedYear;
      }
      return 'Year';
    }
    if (btnPeriod === 'all') return 'All Time';
    return btnPeriod;
  };

  return (
    <div className={styles.timeFilter}>
      <div className={styles.filterRow}>
        <div className={styles.periodButtons}>
          {['week', 'month', 'year', 'all'].map((btn) => (
            <button 
              key={btn}
              className={`${styles.periodBtn} ${period === btn ? styles.active : ''}`}
              onClick={() => handlePeriodClick(btn)}
            >
              {getButtonLabel(btn)}
              {(btn === 'month' || btn === 'year') && period === btn && (
                <span className={styles.dropdownArrow}>▾</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Month Picker Dropdown */}
      {showMonthPicker && period === 'month' && (
        <div className={styles.pickerDropdown}>
          <div className={styles.pickerSection}>
            <span className={styles.pickerLabel}>Month</span>
            <div className={styles.monthGrid}>
              {months.map((month, idx) => {
                const isDisabled = isFutureMonth(idx, selectedYear) || isBeforeFirstLog(idx, selectedYear);
                const hasData = hasDataForMonth(idx, selectedYear);
                
                return (
                  <button
                    key={month}
                    className={`${styles.monthBtn} ${selectedMonth === idx ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
                    onClick={() => handleMonthSelect(idx)}
                    disabled={isDisabled}
                  >
                    {month}
                    {hasData && !isDisabled && <span className={styles.dataDot}></span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className={styles.pickerSection}>
            <span className={styles.pickerLabel}>Year</span>
            <div className={styles.yearList}>
              {yearOptions.map(year => (
                <button
                  key={year}
                  className={`${styles.yearBtn} ${selectedYear === year ? styles.selected : ''} ${hasDataForYear(year) ? styles.hasData : ''}`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Year Picker Dropdown */}
      {showYearPicker && period === 'year' && (
        <div className={styles.pickerDropdown}>
          <div className={styles.pickerSection}>
            <span className={styles.pickerLabel}>Select Year</span>
            <div className={styles.yearList}>
              {yearOptions.map(year => (
                <button
                  key={year}
                  className={`${styles.yearBtn} ${selectedYear === year ? styles.selected : ''} ${hasDataForYear(year) ? styles.hasData : ''}`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeFilter;